import { apiClient } from '../apiClient';
import { ArtifactItemStateHistory } from '../../types/artifactItems';
import { API_ENDPOINTS } from './config';
import {
  ExecutionHistoryEntry,
  StateHistoryEntry,
  TransitionHistoryEntry,
  HistoryFilters,
} from './stateMachineReportingService';

/**
 * Service for managing state transition history
 */
class StateHistoryService {
  /**
   * Get state transition history for an artifact item
   * Backend: GET /artifact-items/{item_id}/state-history (artifact_item_states.py)
   */
  static async getItemStateHistory(itemId: string, _companyId?: string): Promise<ArtifactItemStateHistory[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COMPANY_INSTANCES.ITEM_HISTORY(itemId));

      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (response.data?.history) {
        return response.data.history;
      }
      if (response.data?.items) {
        return response.data.items;
      }

      return [];
    } catch (error: unknown) {
      console.error('❌ [StateHistoryService] Failed to get state history:', error);
      // Return empty array on error rather than throwing
      return [];
    }
  }

  /**
   * Get state history for all items in an artifact
   * This aggregates history from all items
   */
  static async getStateHistoryByArtifact(artifactId: string): Promise<ArtifactItemStateHistory[]> {
    try {
      // First get all items for the artifact
      const itemsResponse = await apiClient.get(`/artifacts/${artifactId}/items`);
      const items = Array.isArray(itemsResponse.data)
        ? itemsResponse.data
        : itemsResponse.data?.items || [];

      // Fetch history for all items in parallel
      const historyPromises = items.map((item: { id: string }) =>
        this.getItemStateHistory(item.id).catch(() => [])
      );

      const allHistories = await Promise.all(historyPromises);

      // Flatten and sort by transition date
      const flattened = allHistories.flat();
      return flattened.sort((a, b) => {
        const dateA = new Date(a.transitioned_at || a.created_at || 0).getTime();
        const dateB = new Date(b.transitioned_at || b.created_at || 0).getTime();
        return dateB - dateA; // Most recent first
      });
    } catch (error: unknown) {
      console.error('❌ [StateHistoryService] Failed to get artifact state history:', error);
      return [];
    }
  }

  /**
   * Get execution history for an artifact
   */
  static async getArtifactExecutionHistory(
    artifactId: string,
    filters?: HistoryFilters
  ): Promise<ExecutionHistoryEntry[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.from_date) params.append('from_date', filters.from_date);
      if (filters?.to_date) params.append('to_date', filters.to_date);
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.STATE_MACHINE.EXECUTION_HISTORY(artifactId)}?${params.toString()}`
        : API_ENDPOINTS.STATE_MACHINE.EXECUTION_HISTORY(artifactId);

      const response = await apiClient.get(endpoint);
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (response.data?.history) {
        return response.data.history;
      }
      return [];
    } catch (error: unknown) {
      console.error('❌ [StateHistoryService] Failed to get artifact execution history:', error);
      return [];
    }
  }

  /**
   * Get state history for an artifact
   */
  static async getArtifactStateHistory(
    artifactId: string,
    filters?: HistoryFilters
  ): Promise<StateHistoryEntry[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.from_date) params.append('from_date', filters.from_date);
      if (filters?.to_date) params.append('to_date', filters.to_date);
      if (filters?.state_id) params.append('state_id', filters.state_id);
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.STATE_MACHINE.STATE_HISTORY(artifactId)}?${params.toString()}`
        : API_ENDPOINTS.STATE_MACHINE.STATE_HISTORY(artifactId);

      const response = await apiClient.get(endpoint);
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (response.data?.history) {
        return response.data.history;
      }
      return [];
    } catch (error: unknown) {
      console.error('❌ [StateHistoryService] Failed to get artifact state history:', error);
      return [];
    }
  }

  /**
   * Get transition history for an artifact
   */
  static async getArtifactTransitionHistory(
    artifactId: string,
    filters?: HistoryFilters
  ): Promise<TransitionHistoryEntry[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.from_date) params.append('from_date', filters.from_date);
      if (filters?.to_date) params.append('to_date', filters.to_date);
      if (filters?.transition_id) params.append('transition_id', filters.transition_id);
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.STATE_MACHINE.TRANSITION_HISTORY(artifactId)}?${params.toString()}`
        : API_ENDPOINTS.STATE_MACHINE.TRANSITION_HISTORY(artifactId);

      const response = await apiClient.get(endpoint);
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (response.data?.history) {
        return response.data.history;
      }
      return [];
    } catch (error: unknown) {
      console.error('❌ [StateHistoryService] Failed to get artifact transition history:', error);
      return [];
    }
  }

  /**
   * Get workflow progress for an artifact
   */
  static async getArtifactWorkflowProgress(artifactId: string): Promise<any> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.STATE_MACHINE.WORKFLOW_PROGRESS(artifactId));
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [StateHistoryService] Failed to get artifact workflow progress:', error);
      return null;
    }
  }
}

export { StateHistoryService };

